import React from 'react';
import { inject, observer } from 'mobx-react';
import { Box, Checkbox, Flex, Select, Text } from 'core/components';
import { TestLike } from '../panels/synthUdeMetrics/buildMetricQueries';
import AgentSelect, { AgentSelectMode } from './AgentSelect';
import useSynthFilterOptions from './useSynthFilterOptions';
import { synthFilterDefs } from './synthFilterDefs';
import { FilterOption, FilterOptionCtx, FilterState, SynthFilterId } from './types';

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <Text as="div" small muted mb="4px" fontWeight="bold" style={{ whiteSpace: 'nowrap' }}>
      {children}
    </Text>
  );
}

// Strip the row hover/selected background so selection is shown only by the checkbox,
// matching AgentSelect.
function decorateOptions(opts: FilterOption[]): FilterOption[] {
  return opts.map((opt) => ({ ...opt, style: { ...opt.style, background: 'transparent' } }));
}

// Pin currently-selected options to the top under a bold "Selected" header, like AgentSelect.
function groupSelectedFirst(opts: FilterOption[], values: string[]): FilterOption[] {
  if (values.length === 0) {
    return opts;
  }
  const selectedSet = new Set(values.map(String));
  const selected = opts.filter((o) => selectedSet.has(String(o.value)));
  if (selected.length === 0) {
    return opts;
  }
  const unselected = opts.filter((o) => !selectedSet.has(String(o.value)));
  const header: FilterOption = {
    value: '__header_selected',
    label: `Selected (${selected.length})`,
    filterLabel: 'Selected',
    isGroupHeader: true,
    groupHeaderProps: { py: '6px', px: '8px', fontWeight: 'bold', fontSize: '11px', bg: 'transparent' }
  };
  return [header, ...selected, ...unselected];
}

// Wrap a filter's option content with a leading checkbox reflecting its selected state.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function makeCheckboxFormatter(inner?: (option: any) => React.ReactNode) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return function checkboxFormatter(option: any) {
    if (option.groupHeaderProps) {
      return undefined; // let Select render the group header
    }
    return (
      <Flex alignItems="center" gap={1} overflow="hidden" width="100%">
        <Checkbox checked={option.selected} readOnly m={0} style={{ flexShrink: 0, pointerEvents: 'none' }} />
        <Box flex="1 1 auto" minWidth={0}>
          {inner ? (
            inner(option)
          ) : (
            <Text small ellipsis>
              {option.label}
            </Text>
          )}
        </Box>
      </Flex>
    );
  };
}

// With a max of 1 the newest pick replaces the old one, so switching doesn't require deselecting first.
export function capSelection(next: string[], max?: number): string[] {
  if (!max || next.length <= max) {
    return next;
  }
  return max === 1 ? next.slice(-1) : next.slice(0, max);
}

interface SynthFilterBarProps {
  test: TestLike;
  /** Ordered filter ids to render (see tabFilterConfig). Unavailable ones auto-hide. */
  filters: SynthFilterId[];
  /** Optional uppercase heading above the fields (e.g. "Filter Metrics"). */
  title?: string;
  /** Extra controls rendered inline at the end of the field row (e.g. Series Depth). */
  trailingControls?: React.ReactNode;
  /** Selection mode for the Source Agent field. Defaults to 'multi'. */
  agentSelectMode?: AgentSelectMode;
  /** Cap the number of selectable agents (multi mode). */
  agentMaxSelected?: number;
  /** Cap the number of selectable targets; hides "Select all". */
  targetMaxSelected?: number;
  /** Stretch to fill the row (default true). Set false to size to content when rendered inline. */
  fullWidth?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  $syn?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  $labels?: any;
}

const SynthFilterBar = inject(
  '$syn',
  '$labels'
)(
  observer(
    ({
      test,
      filters,
      title,
      trailingControls,
      agentSelectMode = 'multi',
      agentMaxSelected,
      targetMaxSelected,
      fullWidth = true,
      $syn,
      $labels
    }: SynthFilterBarProps) => {
      const state = test.results as unknown as FilterState;
      const options = useSynthFilterOptions({ test, $syn, $labels });
      const ctx: FilterOptionCtx = { test, ...options };

      const visibleDefs = filters.map((id) => synthFilterDefs[id]).filter((def) => def && def.isAvailable(ctx));

      return (
        <Box width={fullWidth ? '100%' : undefined}>
          {title && (
            <Text
              as="div"
              fontSize="11px"
              muted
              mb="6px"
              style={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}
            >
              {title}
            </Text>
          )}
          <Flex width={fullWidth ? '100%' : undefined} gap="12px" alignItems="flex-end">
            {/* Metrics bar wraps its fields; the inline (Traceroute) bar stays on one row and truncates tags */}
            <Flex
              gap="12px"
              alignItems="flex-end"
              flexWrap={fullWidth ? 'wrap' : 'nowrap'}
              flex="1 1 auto"
              minWidth={0}
            >
              {visibleDefs.map((def) => {
                const values = def.getValues(state);
                const opts = def.getOptions(ctx);
                const maxValues = def.id === 'target' ? targetMaxSelected : undefined;
                const handleChange = (next: string[]) => def.setValues(state, capSelection(next, maxValues));

                return (
                  <Box key={def.id} minWidth={def.field.minWidth} flex={def.field.flex} maxWidth={def.field.maxWidth}>
                    <FieldLabel>{def.label(test)}</FieldLabel>
                    {def.renderer === 'agentSelect' ? (
                      <AgentSelect
                        agentOptions={opts}
                        values={values}
                        onChange={handleChange}
                        mode={agentSelectMode}
                        maxValues={agentMaxSelected}
                        placeholder={def.placeholder(test)}
                      />
                    ) : (
                      <Select
                        multi
                        keepOpen
                        toggle
                        showFilter
                        showClearButton
                        showSelectAll={def.showSelectAll && !maxValues}
                        fill
                        menuWidth={def.menuWidth}
                        options={decorateOptions(groupSelectedFirst(opts, values))}
                        values={values}
                        onChange={handleChange}
                        placeholder={def.placeholder(test)}
                        optionFormatter={makeCheckboxFormatter(def.optionFormatter)}
                        disabled={def.disabledWhenEmpty && opts.length === 0}
                      />
                    )}
                  </Box>
                );
              })}
            </Flex>
            {/* Trailing controls (e.g. Series Depth / Chart Size) pinned to the right */}
            {trailingControls && <Box flex="0 0 auto">{trailingControls}</Box>}
          </Flex>
        </Box>
      );
    }
  )
);

export default SynthFilterBar;
